import { Drawer } from '@base-ui/react/drawer';
import maplibregl, {
  type ExpressionSpecification,
  type Map as MapLibreMap,
  type Marker as MapLibreMarker,
  type StyleSpecification,
} from 'maplibre-gl';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { FeatureCollection, MultiLineString } from 'geojson';
import Lightbox, { type Slide } from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import {
  type Activity,
  type Photo,
  type RecordedRoute,
  activityIdSchema,
  fetchAndParse,
  photoManifestSchema,
  recordedRouteSchema,
  walkDateSchema,
} from './schemas';
import {
  ChartFrame,
  ChartTooltip,
  CloseButton,
  DayStats,
  DrawerContent,
  DrawerDate,
  DrawerHandle,
  DrawerHeader,
  DrawerNavigation,
  DrawerNavigationButton,
  DrawerPopup,
  DrawerTitle,
  DrawerViewport,
  EmptyAlbum,
  Experience,
  LightboxCounter,
  LightboxMeta,
  MapCanvas,
  MapTools,
  OverviewTitle,
  PaperGrain,
  PhotoGallery,
  PhotoGalleryThumb,
  PhotoSection,
  Section,
  SectionHeading,
  Stats,
  StoryPanel,
} from './styles';

const terrainStyle: StyleSpecification = {
  version: 8,
  name: 'Japan relief',
  projection: { type: 'mercator' },
  sources: {
    satellite: {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      attribution: 'Imagery © Esri and contributors',
      maxzoom: 18,
    },
    dem: {
      type: 'raster-dem',
      tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
      tileSize: 256,
      encoding: 'terrarium',
      attribution: 'Elevation © AWS Open Data / Mapzen',
      maxzoom: 15,
    },
  },
  layers: [
    {
      id: 'satellite',
      type: 'raster',
      source: 'satellite',
      paint: {
        'raster-saturation': 0.12,
        'raster-contrast': 0.08,
        'raster-brightness-min': 0.12,
        'raster-brightness-max': 1,
      },
    },
    {
      id: 'hillshade',
      type: 'hillshade',
      source: 'dem',
      paint: {
        'hillshade-exaggeration': 0.46,
        'hillshade-shadow-color': '#282b28',
        'hillshade-highlight-color': '#f2ead9',
        'hillshade-accent-color': '#8b8575',
      },
    },
  ],
  sky: { 'sky-color': '#eee8dc', 'sky-horizon-blend': 0.72, 'horizon-color': '#d9d4c9' },
};

const japanView: { center: [number, number]; zoom: number; pitch: number; bearing: number } = {
  center: [137.8, 36.4],
  zoom: 4.45,
  pitch: 38,
  bearing: -7,
};
const numberFormat = new Intl.NumberFormat('en-AU');
const wholeNumberFormat = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 });
const decimalFormat = new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dateFormat = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'Asia/Tokyo',
});

type RouteSummary = {
  distanceKm: number;
  elevationGainM: number;
  started: string;
  finished: string;
};

type RestDay = {
  date: string;
  location: string;
};

type CalendarDay = {
  date: string;
  title: string;
  activity: Activity | null;
  restDay: RestDay | null;
};

const restDayLocations: Readonly<Record<string, string>> = {
  '2026-05-21': 'Niigata',
};
const mobileDrawerSnapPoints: Drawer.Root.SnapPoint[] = ['13.5rem', 1];

type LocatedPhoto = Photo & { lat: number; lon: number };
type PhotoMarkerCluster = {
  id: string;
  anchor: LocatedPhoto;
  photos: LocatedPhoto[];
};
const elevationPlot = { top: 10, right: 8, bottom: 30, left: 42 };

function hasPhotoLocation(photo: Photo): photo is LocatedPhoto {
  return photo.lat !== null && photo.lon !== null;
}

function evenlySample<T>(items: T[], limit: number): T[] {
  if (limit <= 0 || items.length === 0) return [];
  if (items.length <= limit) return items;
  if (limit === 1) {
    const middleItem = items[Math.floor((items.length - 1) / 2)];
    return middleItem === undefined ? [] : [middleItem];
  }
  return Array.from(
    { length: limit },
    (_, index) => items[Math.round((index / (limit - 1)) * (items.length - 1))],
  ).filter((item): item is T => item !== undefined);
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function distanceBetweenPhotos(first: LocatedPhoto, second: LocatedPhoto): number {
  const earthRadiusM = 6_371_000;
  const latitudeDelta = toRadians(second.lat - first.lat);
  const longitudeDelta = toRadians(second.lon - first.lon);
  const latitudeA = toRadians(first.lat);
  const latitudeB = toRadians(second.lat);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(latitudeA) * Math.cos(latitudeB) * Math.sin(longitudeDelta / 2) ** 2;
  return 2 * earthRadiusM * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

function clusterPhotosByLocation(photos: LocatedPhoto[], radiusM = 20): PhotoMarkerCluster[] {
  const photosByDate = new Map<string, LocatedPhoto[]>();
  for (const photo of photos) {
    const dayPhotos = photosByDate.get(photo.capture_date) ?? [];
    dayPhotos.push(photo);
    photosByDate.set(photo.capture_date, dayPhotos);
  }

  return [...photosByDate.values()].flatMap((dayPhotos) => {
    const clusters: LocatedPhoto[][] = [];
    for (const photo of dayPhotos) {
      const cluster = clusters.find((candidate) => {
        const anchor = candidate[0];
        return anchor ? distanceBetweenPhotos(anchor, photo) <= radiusM : false;
      });
      if (cluster) cluster.push(photo);
      else clusters.push([photo]);
    }

    return clusters.flatMap((clusterPhotos) => {
      const anchor = clusterPhotos.find((photo) => photo.orientation === 'landscape') ?? clusterPhotos[0];
      const orderedPhotos = anchor
        ? [anchor, ...clusterPhotos.filter((photo) => photo.id !== anchor.id)]
        : [];
      return anchor ? [{ id: anchor.id, anchor, photos: orderedPhotos }] : [];
    });
  });
}

function clusterPhotoMarkersOnScreen(
  map: MapLibreMap,
  markers: PhotoMarkerCluster[],
  radiusPx: number,
): PhotoMarkerCluster[][] {
  const projected = new Map(
    markers.map((marker) => [marker.id, map.project([marker.anchor.lon, marker.anchor.lat])]),
  );
  const clusters: PhotoMarkerCluster[][] = [];

  for (const marker of markers) {
    const point = projected.get(marker.id);
    if (!point) continue;
    const cluster = clusters.find((candidate) => {
      const anchor = candidate[0];
      const anchorPoint = anchor ? projected.get(anchor.id) : undefined;
      return anchorPoint ? point.dist(anchorPoint) <= radiusPx : false;
    });
    if (cluster) cluster.push(marker);
    else clusters.push([marker]);
  }

  return clusters;
}

function photoClusterExpansionCamera(
  map: MapLibreMap,
  cluster: PhotoMarkerCluster[],
  radiusPx: number,
): { center: [number, number]; zoom: number } | null {
  const maximumExpansionZoom = 12;
  const currentZoom = map.getZoom();
  if (currentZoom >= maximumExpansionZoom - 0.15) return null;

  const projected = cluster.map((marker) => map.project([marker.anchor.lon, marker.anchor.lat]));
  let minimumDistancePx = Number.POSITIVE_INFINITY;
  for (const [index, point] of projected.entries()) {
    for (const otherPoint of projected.slice(index + 1)) {
      minimumDistancePx = Math.min(minimumDistancePx, point.dist(otherPoint));
    }
  }
  const requiredZoomDelta = Math.max(0.85, Math.log2((radiusPx + 10) / Math.max(1, minimumDistancePx)) + 0.2);
  const zoom = Math.min(maximumExpansionZoom, currentZoom + Math.min(2.25, requiredZoomDelta));
  if (zoom <= currentZoom + 0.15) return null;

  const coordinates = cluster.map((marker): [number, number] => [marker.anchor.lon, marker.anchor.lat]);
  const firstCoordinate = coordinates[0];
  if (!firstCoordinate) return null;
  const bounds = coordinates.reduce(
    (result, coordinate) => result.extend(coordinate),
    new maplibregl.LngLatBounds(firstCoordinate, firstCoordinate),
  );
  const center = bounds.getCenter();
  return { center: [center.lng, center.lat], zoom };
}

function markerDensityTiers<T extends { id: string }>(
  items: T[],
  coreLimit: number,
  mediumLimit: number,
): Map<string, 0 | 1 | 2> {
  const core = new Set(evenlySample(items, coreLimit).map((item) => item.id));
  const mediumCandidates = items.filter((item) => !core.has(item.id));
  const medium = new Set(
    evenlySample(mediumCandidates, Math.max(0, mediumLimit - core.size)).map((item) => item.id),
  );
  return new Map(items.map((item) => [item.id, core.has(item.id) ? 0 : medium.has(item.id) ? 1 : 2]));
}

function inclusiveDayCount(started: string, finished: string): number {
  const millisecondsPerDay = 86_400_000;
  const start = Date.parse(`${started}T00:00:00Z`);
  const finish = Date.parse(`${finished}T00:00:00Z`);
  return Math.floor((finish - start) / millisecondsPerDay) + 1;
}

function useMobileDrawer() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 700px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 700px)');
    const update = () => setIsMobile(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return isMobile;
}

function publicGeoJson(route: RecordedRoute): FeatureCollection<MultiLineString> {
  return { type: 'FeatureCollection', features: route.features };
}

function addRecordedRoute(map: MapLibreMap, route: RecordedRoute) {
  map.addSource('recorded-route', { type: 'geojson', data: publicGeoJson(route) });
  map.addLayer({
    id: 'recorded-route-shadow',
    type: 'line',
    source: 'recorded-route',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#2b160e',
      'line-width': ['interpolate', ['linear'], ['zoom'], 4, 4, 10, 8.6],
      'line-opacity': 0.58,
      'line-blur': 2.4,
    },
  });
  map.addLayer({
    id: 'recorded-route-line',
    type: 'line',
    source: 'recorded-route',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#b54532',
      'line-width': ['interpolate', ['linear'], ['zoom'], 4, 1.9, 10, 5.2],
      'line-opacity': 0.98,
    },
  });
  map.addLayer({
    id: 'recorded-route-hit',
    type: 'line',
    source: 'recorded-route',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#b54532', 'line-width': 18, 'line-opacity': 0 },
  });

  map.addSource('elevation-hover', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: 'elevation-hover-halo',
    type: 'circle',
    source: 'elevation-hover',
    paint: {
      'circle-radius': 11,
      'circle-color': '#fff1d0',
      'circle-opacity': 0.72,
      'circle-blur': 0.45,
    },
  });
  map.addLayer({
    id: 'elevation-hover-point',
    type: 'circle',
    source: 'elevation-hover',
    paint: {
      'circle-radius': 5,
      'circle-color': '#b54532',
      'circle-stroke-color': '#fff8e9',
      'circle-stroke-width': 2,
    },
  });
}

function setElevationLocation(map: MapLibreMap, coordinate: [number, number] | null) {
  const source = map.getSource('elevation-hover');
  if (!(source instanceof maplibregl.GeoJSONSource)) return;
  source.setData({
    type: 'FeatureCollection',
    features: coordinate
      ? [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: coordinate } }]
      : [],
  });
}

function setSelectedRoute(map: MapLibreMap, activityId: string | null, selectedDate: string | null = null) {
  if (!map.getLayer('recorded-route-line')) return;
  const selected: ExpressionSpecification = ['==', ['get', 'activity_id'], activityId];
  const comparisonDate = selectedDate ?? '';
  const earlier: ExpressionSpecification = ['<', ['to-string', ['get', 'date']], comparisonDate];
  const sameDay: ExpressionSpecification = ['==', ['get', 'date'], comparisonDate];
  const hasSelection = selectedDate !== null;
  const lineOpacity: ExpressionSpecification | number = hasSelection ? ['case', selected, 1, 0.7] : 0.98;
  const shadowOpacity: ExpressionSpecification | number = hasSelection
    ? ['case', selected, 0.42, 0.22]
    : 0.58;
  const lineWidth: ExpressionSpecification = hasSelection
    ? [
        'interpolate',
        ['linear'],
        ['zoom'],
        4,
        ['case', selected, 2.5, earlier, 1.2, sameDay, 1.6, 0.65],
        10,
        ['case', selected, 4.6, earlier, 2.2, sameDay, 3, 1.1],
      ]
    : ['interpolate', ['linear'], ['zoom'], 4, 1.9, 10, 5.2];
  const lineColor: ExpressionSpecification | string = hasSelection
    ? ['case', selected, '#ff7a3d', earlier, '#b86b49', sameDay, '#d96a3b', '#8c766a']
    : '#b54532';
  map.setPaintProperty('recorded-route-line', 'line-opacity', lineOpacity);
  map.setPaintProperty('recorded-route-line', 'line-width', lineWidth);
  map.setPaintProperty('recorded-route-line', 'line-color', lineColor);
  map.setPaintProperty('recorded-route-shadow', 'line-opacity', shadowOpacity);
}

function focusActivity(map: MapLibreMap, route: RecordedRoute, activityId: string, isMobile: boolean) {
  const feature = route.features.find((item) => item.properties.activity_id === activityId);
  if (!feature) return;
  const points = feature.geometry.coordinates.flat();
  const bounds = points.reduce(
    (result, coordinate) => result.extend(coordinate),
    new maplibregl.LngLatBounds(points[0], points[0]),
  );
  map.fitBounds(bounds, {
    padding: isMobile
      ? { top: 50, right: 24, bottom: Math.round(window.innerHeight * 0.48), left: 24 }
      : { top: 48, right: 470, bottom: 48, left: 48 },
    maxZoom: 11.5,
    duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 650,
  });
}

function focusWholeRoute(map: MapLibreMap, route: RecordedRoute, isMobile: boolean, animate = true) {
  const points = route.features.flatMap((feature) => feature.geometry.coordinates.flat());
  const firstPoint = points[0];
  if (!firstPoint) return;
  const bounds = points.reduce(
    (result, coordinate) => result.extend(coordinate),
    new maplibregl.LngLatBounds(firstPoint, firstPoint),
  );
  const desktopLeftPadding = Math.min(560, Math.round(window.innerWidth * 0.48));
  map.fitBounds(bounds, {
    padding: isMobile
      ? { top: 76, right: 18, bottom: 218, left: 18 }
      : { top: 70, right: 70, bottom: 70, left: desktopLeftPadding },
    pitch: isMobile ? 24 : map.getPitch(),
    bearing: isMobile ? 38 : map.getBearing(),
    maxZoom: 6.5,
    duration: !animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 900,
  });
}

function focusPhotos(map: MapLibreMap, photos: Photo[], isMobile: boolean) {
  const located = photos.filter(hasPhotoLocation);
  const firstPhoto = located[0];
  if (!firstPhoto) return;
  const bounds = located.reduce(
    (result, photo) => result.extend([photo.lon, photo.lat]),
    new maplibregl.LngLatBounds([firstPhoto.lon, firstPhoto.lat], [firstPhoto.lon, firstPhoto.lat]),
  );
  map.fitBounds(bounds, {
    padding: isMobile
      ? { top: 50, right: 24, bottom: Math.round(window.innerHeight * 0.48), left: 24 }
      : { top: 48, right: 470, bottom: 48, left: 48 },
    maxZoom: 12,
    duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 650,
  });
}

function ElevationChart({
  activity,
  highlightedLocation,
  onLocationChange,
}: {
  activity: Activity;
  highlightedLocation: [number, number] | null;
  onLocationChange: (coordinate: [number, number] | null) => void;
}) {
  const gradientId = useId().replaceAll(':', '');
  const profile = activity.elevation_profile;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const width = 360;
  const height = 164;

  const drawing = useMemo(() => {
    if (profile.length < 2) return null;
    const maxDistance = Math.max(...profile.map(([distance]) => distance), 1);
    const elevations = profile.map(([, elevation]) => elevation);
    const minimum = Math.min(...elevations);
    const maximum = Math.max(...elevations);
    const range = Math.max(maximum - minimum, 1);
    const plotWidth = width - elevationPlot.left - elevationPlot.right;
    const plotHeight = height - elevationPlot.top - elevationPlot.bottom;
    const coordinates = profile.map(([distance, elevation]): [number, number] => [
      elevationPlot.left + (distance / maxDistance) * plotWidth,
      elevationPlot.top + ((maximum - elevation) / range) * plotHeight,
    ]);
    const firstCoordinate = coordinates[0];
    if (!firstCoordinate) return null;
    const line = `M ${coordinates.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L ')}`;
    const baseline = height - elevationPlot.bottom;
    const area = `${line} L ${coordinates.at(-1)?.[0] ?? width} ${baseline} L ${firstCoordinate[0]} ${baseline} Z`;
    const xTicks = [0, maxDistance / 2, maxDistance];
    const yTicks = [minimum, minimum + range / 2, maximum];
    return { line, area, coordinates, maximum, minimum, maxDistance, range, xTicks, yTicks };
  }, [profile]);
  const highlightedIndex = useMemo(() => {
    if (!highlightedLocation || !profile.length) return null;
    const latitudeScale = Math.cos((highlightedLocation[1] * Math.PI) / 180);
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    profile.forEach(([, , lon, lat], index) => {
      const x = (lon - highlightedLocation[0]) * latitudeScale;
      const y = lat - highlightedLocation[1];
      const distance = x * x + y * y;
      if (distance < nearestDistance) {
        nearestIndex = index;
        nearestDistance = distance;
      }
    });
    return nearestIndex;
  }, [highlightedLocation, profile]);
  const displayIndex = hoveredIndex ?? highlightedIndex;

  if (!drawing) return <EmptyAlbum>Altitude samples weren’t present in this recording.</EmptyAlbum>;

  return (
    <>
      <ChartFrame
        onPointerMove={(event) => {
          const bounds = event.currentTarget.getBoundingClientRect();
          const svgX = ((event.clientX - bounds.left) / bounds.width) * width;
          const progress = Math.max(
            0,
            Math.min(1, (svgX - elevationPlot.left) / (width - elevationPlot.left - elevationPlot.right)),
          );
          const index = Math.round(progress * (profile.length - 1));
          if (index !== hoveredIndex) {
            setHoveredIndex(index);
            const point = profile[index];
            onLocationChange(point ? [point[2], point[3]] : null);
          }
        }}
        onPointerLeave={() => {
          setHoveredIndex(null);
          onLocationChange(null);
        }}
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={`Elevation profile for ${activity.title}. Move across the chart for exact values.`}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#b54532" stopOpacity="0.42" />
              <stop offset="1" stopColor="#b54532" stopOpacity="0.03" />
            </linearGradient>
          </defs>
          {drawing.yTicks.map((tick) => {
            const y =
              elevationPlot.top +
              ((drawing.maximum - tick) / Math.max(drawing.maximum - drawing.minimum, 1)) *
                (height - elevationPlot.top - elevationPlot.bottom);
            return (
              <g key={`y-${tick}`}>
                <line
                  x1={elevationPlot.left}
                  x2={width - elevationPlot.right}
                  y1={y}
                  y2={y}
                  stroke="#3c413b"
                  strokeOpacity="0.13"
                />
                <text x={elevationPlot.left - 6} y={y + 3} fill="#62645d" fontSize="8" textAnchor="end">
                  {numberFormat.format(Math.round(tick))}
                </text>
              </g>
            );
          })}
          {drawing.xTicks.map((tick) => {
            const x =
              elevationPlot.left +
              (tick / drawing.maxDistance) * (width - elevationPlot.left - elevationPlot.right);
            return (
              <g key={`x-${tick}`}>
                <line
                  x1={x}
                  x2={x}
                  y1={height - elevationPlot.bottom}
                  y2={height - elevationPlot.bottom + 4}
                  stroke="#3c413b"
                  strokeOpacity="0.5"
                />
                <text
                  x={x}
                  y={height - elevationPlot.bottom + 14}
                  fill="#62645d"
                  fontSize="8"
                  textAnchor={tick === 0 ? 'start' : tick === drawing.maxDistance ? 'end' : 'middle'}
                >
                  {decimalFormat.format(tick)}
                </text>
              </g>
            );
          })}
          <line
            x1={elevationPlot.left}
            x2={elevationPlot.left}
            y1={elevationPlot.top}
            y2={height - elevationPlot.bottom}
            stroke="#3c413b"
            strokeOpacity="0.42"
          />
          <line
            x1={elevationPlot.left}
            x2={width - elevationPlot.right}
            y1={height - elevationPlot.bottom}
            y2={height - elevationPlot.bottom}
            stroke="#3c413b"
            strokeOpacity="0.42"
          />
          <text
            x="9"
            y={elevationPlot.top + (height - elevationPlot.top - elevationPlot.bottom) / 2}
            fill="#4f564d"
            fontSize="8"
            letterSpacing="0.08em"
            textAnchor="middle"
            transform={`rotate(-90 9 ${elevationPlot.top + (height - elevationPlot.top - elevationPlot.bottom) / 2})`}
          >
            ELEVATION (M)
          </text>
          <text
            x={elevationPlot.left + (width - elevationPlot.left - elevationPlot.right) / 2}
            y={height - 3}
            fill="#4f564d"
            fontSize="8"
            letterSpacing="0.08em"
            textAnchor="middle"
          >
            DISTANCE (KM)
          </text>
          <path d={drawing.area} fill={`url(#${gradientId})`} />
          <path
            d={drawing.line}
            fill="none"
            stroke="#d94e16"
            strokeWidth="2.5"
            vectorEffect="non-scaling-stroke"
          />
          {displayIndex !== null && drawing.coordinates[displayIndex] && (
            <>
              <line
                x1={drawing.coordinates[displayIndex][0]}
                x2={drawing.coordinates[displayIndex][0]}
                y1={elevationPlot.top}
                y2={height - elevationPlot.bottom}
                stroke="#3c413b"
                strokeDasharray="3 3"
                strokeOpacity="0.55"
              />
              <circle
                cx={drawing.coordinates[displayIndex][0]}
                cy={drawing.coordinates[displayIndex][1]}
                r="4.5"
                fill="#eee9d9"
                stroke="#d94e16"
                strokeWidth="2.5"
              />
            </>
          )}
        </svg>
        {displayIndex !== null && profile[displayIndex] && drawing.coordinates[displayIndex] && (
          <ChartTooltip
            $x={(drawing.coordinates[displayIndex][0] / width) * 100}
            $y={(drawing.coordinates[displayIndex][1] / height) * 100}
          >
            <strong>{numberFormat.format(profile[displayIndex][1])} m</strong>
            {decimalFormat.format(profile[displayIndex][0])} km
          </ChartTooltip>
        )}
      </ChartFrame>
    </>
  );
}

function DayPhotos({
  photos,
  journeyPhotos,
  spotlightPhotoId,
  onPhotoHighlightChange,
  onSpotlightClose,
}: {
  photos: Photo[];
  journeyPhotos: Photo[];
  spotlightPhotoId: string | null;
  onPhotoHighlightChange: (photo: Photo | null) => void;
  onSpotlightClose: () => void;
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const activePhoto = activeIndex === null ? null : journeyPhotos[activeIndex];
  const slides = useMemo<Slide[]>(
    () =>
      journeyPhotos.map((photo, index) => ({
        src: photo.spotlight_path,
        alt: `Japan ride on ${photo.capture_date}, journey photo ${index + 1} of ${journeyPhotos.length}`,
        ...(photo.width && photo.height ? { width: photo.width, height: photo.height } : {}),
      })),
    [journeyPhotos],
  );
  const activeDayPosition = useMemo(() => {
    if (!activePhoto) return null;
    const dayPhotos = journeyPhotos.filter((photo) => photo.capture_date === activePhoto.capture_date);
    return {
      index: dayPhotos.findIndex((photo) => photo.id === activePhoto.id),
      count: dayPhotos.length,
    };
  }, [activePhoto, journeyPhotos]);

  useEffect(() => {
    if (!spotlightPhotoId) return;
    const index = journeyPhotos.findIndex((photo) => photo.id === spotlightPhotoId);
    if (index >= 0) setActiveIndex(index);
  }, [journeyPhotos, spotlightPhotoId]);

  if (!photos.length) return <EmptyAlbum>No photos matched this day.</EmptyAlbum>;

  return (
    <>
      <PhotoGallery aria-label="Photos from this day" onPointerLeave={() => onPhotoHighlightChange(null)}>
        {photos.map((photo, index) => (
          <PhotoGalleryThumb
            key={photo.id}
            type="button"
            aria-label={`Open photo ${index + 1} of ${photos.length} full screen`}
            onClick={() => {
              const journeyIndex = journeyPhotos.findIndex((item) => item.id === photo.id);
              if (journeyIndex >= 0) setActiveIndex(journeyIndex);
            }}
            onPointerEnter={() => onPhotoHighlightChange(photo)}
            onFocus={() => onPhotoHighlightChange(photo)}
            onBlur={() => onPhotoHighlightChange(null)}
          >
            <img src={photo.thumbnail_path} alt="" loading="lazy" decoding="async" />
          </PhotoGalleryThumb>
        ))}
      </PhotoGallery>

      <Lightbox
        open={activePhoto !== null}
        close={() => {
          setActiveIndex(null);
          onSpotlightClose();
        }}
        index={activeIndex ?? 0}
        slides={slides}
        on={{ view: ({ index }) => setActiveIndex(index) }}
        carousel={{
          finite: true,
          preload: Math.min(4, Math.max(1, journeyPhotos.length - 1)),
          padding: '4%',
          spacing: '12%',
          imageFit: 'contain',
        }}
        controller={{ closeOnBackdropClick: true, closeOnEscape: true }}
        animation={{
          fade: 220,
          swipe: 350,
          navigation: 260,
          easing: {
            fade: 'ease-out',
            swipe: 'cubic-bezier(0.16, 1, 0.3, 1)',
            navigation: 'cubic-bezier(0.16, 1, 0.3, 1)',
          },
        }}
        className="japan-lightbox"
        labels={{ Next: 'Next photo', Previous: 'Previous photo', Close: 'Close photo viewer' }}
        render={{
          controls: () =>
            activePhoto && activeIndex !== null && activeDayPosition ? (
              <LightboxMeta>
                <span>{dateFormat.format(new Date(`${activePhoto.capture_date}T12:00:00+12:00`))}</span>
                <LightboxCounter>
                  {String(activeDayPosition.index + 1).padStart(2, '0')} /{' '}
                  {String(activeDayPosition.count).padStart(2, '0')}
                </LightboxCounter>
              </LightboxMeta>
            ) : null,
        }}
      />
    </>
  );
}

function ActivityDrawer({
  activity,
  restDay,
  photos,
  journeyPhotos,
  previousDay,
  nextDay,
  spotlightPhotoId,
  isMobile,
  onClose,
  onNavigate,
  onSpotlightClose,
  onPhotoMarkerHighlightChange,
  onElevationLocationChange,
}: {
  activity: Activity | null;
  restDay: RestDay | null;
  photos: Photo[];
  journeyPhotos: Photo[];
  previousDay: CalendarDay | null;
  nextDay: CalendarDay | null;
  spotlightPhotoId: string | null;
  isMobile: boolean;
  onClose: () => void;
  onNavigate: (day: CalendarDay) => void;
  onSpotlightClose: () => void;
  onPhotoMarkerHighlightChange: (photoId: string | null) => void;
  onElevationLocationChange: (coordinate: [number, number] | null) => void;
}) {
  const [highlightedPhotoLocation, setHighlightedPhotoLocation] = useState<[number, number] | null>(null);
  const [mobileSnapPoint, setMobileSnapPoint] = useState<Drawer.Root.SnapPoint | null>(1);
  const dayDate = activity?.date ?? restDay?.date ?? null;
  const dayTitle = activity?.title ?? restDay?.location ?? 'Ride day';

  const handlePhotoHighlightChange = (photo: Photo | null) => {
    const coordinate: [number, number] | null =
      photo && hasPhotoLocation(photo) ? [photo.lon, photo.lat] : null;
    setHighlightedPhotoLocation(coordinate);
    onPhotoMarkerHighlightChange(photo?.id ?? null);
    onElevationLocationChange(coordinate);
  };

  return (
    <Drawer.Root
      open={dayDate !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      swipeDirection={isMobile ? 'down' : 'right'}
      snapPoints={isMobile ? mobileDrawerSnapPoints : undefined}
      snapPoint={isMobile ? mobileSnapPoint : undefined}
      onSnapPointChange={(snapPoint) => {
        if (isMobile) setMobileSnapPoint(snapPoint);
      }}
      snapToSequentialPoints
      modal={false}
      disablePointerDismissal
    >
      <Drawer.Portal>
        <DrawerViewport>
          <DrawerPopup initialFocus={false}>
            {dayDate && (
              <>
                <CloseButton aria-label="Close activity details">×</CloseButton>
                <DrawerHandle
                  type="button"
                  aria-label={
                    mobileSnapPoint === 1
                      ? 'Collapse day details to show more of the map'
                      : 'Expand day details'
                  }
                  onClick={() => setMobileSnapPoint(mobileSnapPoint === 1 ? '13.5rem' : 1)}
                />
                <DrawerContent>
                  <DrawerHeader>
                    <DrawerDate>
                      {restDay ? 'Rest day · ' : ''}
                      {dateFormat.format(new Date(`${dayDate}T12:00:00+12:00`))}
                    </DrawerDate>
                    <DrawerTitle>{dayTitle}</DrawerTitle>
                  </DrawerHeader>

                  {activity && (
                    <>
                      <DayStats>
                        <div>
                          <dt>Distance</dt>
                          <dd>{decimalFormat.format(activity.distance_km)} km</dd>
                        </div>
                        <div>
                          <dt>Climbed</dt>
                          <dd>{numberFormat.format(activity.elevation_gain_m)} m</dd>
                        </div>
                        <div>
                          <dt>Lowest</dt>
                          <dd>
                            {activity.min_elevation_m === null
                              ? '—'
                              : `${numberFormat.format(activity.min_elevation_m)} m`}
                          </dd>
                        </div>
                        <div>
                          <dt>Highest</dt>
                          <dd>
                            {activity.max_elevation_m === null
                              ? '—'
                              : `${numberFormat.format(activity.max_elevation_m)} m`}
                          </dd>
                        </div>
                      </DayStats>

                      <Section>
                        <SectionHeading>
                          <h3>Altitude</h3>
                        </SectionHeading>
                        <ElevationChart
                          activity={activity}
                          highlightedLocation={highlightedPhotoLocation}
                          onLocationChange={onElevationLocationChange}
                        />
                      </Section>
                    </>
                  )}

                  <PhotoSection>
                    <SectionHeading>
                      <h3>Photos</h3>
                    </SectionHeading>
                    <DayPhotos
                      photos={photos}
                      journeyPhotos={journeyPhotos}
                      spotlightPhotoId={spotlightPhotoId}
                      onPhotoHighlightChange={handlePhotoHighlightChange}
                      onSpotlightClose={onSpotlightClose}
                    />
                  </PhotoSection>
                </DrawerContent>
                <DrawerNavigation
                  $collapsed={isMobile && mobileSnapPoint !== 1}
                  aria-label="Browse ride days"
                >
                  <DrawerNavigationButton
                    type="button"
                    disabled={!previousDay}
                    onClick={() => previousDay && onNavigate(previousDay)}
                  >
                    <span>← Previous day</span>
                    <strong>{previousDay?.title ?? 'Start of ride'}</strong>
                  </DrawerNavigationButton>
                  <DrawerNavigationButton
                    type="button"
                    $align="right"
                    disabled={!nextDay}
                    onClick={() => nextDay && onNavigate(nextDay)}
                  >
                    <span>Next day →</span>
                    <strong>{nextDay?.title ?? 'End of ride'}</strong>
                  </DrawerNavigationButton>
                </DrawerNavigation>
              </>
            )}
          </DrawerPopup>
        </DrawerViewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

export function App() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const routeRef = useRef<RecordedRoute | null>(null);
  const selectedActivityRef = useRef<Activity | null>(null);
  const selectedDayDateRef = useRef<string | null>(null);
  const highlightedPhotoIdRef = useRef<string | null>(null);
  const [photoMarkerElements] = useState(() => new Map<string, HTMLButtonElement>());
  const closeActivityRef = useRef<() => void>(() => undefined);
  const openActivityRef = useRef<(activity: Activity) => void>(() => undefined);
  const openRestDayRef = useRef<(restDay: RestDay) => void>(() => undefined);
  const [activitiesByDate] = useState(() => new Map<string, Activity>());
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [selectedRestDay, setSelectedRestDay] = useState<RestDay | null>(null);
  const [spotlightPhotoId, setSpotlightPhotoId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [summary, setSummary] = useState<RouteSummary | null>(null);
  const [isThreeDimensional, setIsThreeDimensional] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const isMobile = useMobileDrawer();
  const selectedDayDate = selectedActivity?.date ?? selectedRestDay?.date ?? null;

  useEffect(() => {
    selectedActivityRef.current = selectedActivity;
    selectedDayDateRef.current = selectedDayDate;
  }, [selectedActivity, selectedDayDate]);

  const setHighlightedPhotoMarker = (photoId: string | null) => {
    const previousElement = highlightedPhotoIdRef.current
      ? photoMarkerElements.get(highlightedPhotoIdRef.current)
      : undefined;
    const nextElement = photoId ? photoMarkerElements.get(photoId) : undefined;
    highlightedPhotoIdRef.current = photoId;
    if (previousElement && previousElement !== nextElement) previousElement.dataset.highlighted = 'false';
    if (nextElement) nextElement.dataset.highlighted = 'true';
  };

  const closeActivity = () => {
    selectedActivityRef.current = null;
    selectedDayDateRef.current = null;
    setSelectedActivity(null);
    setSelectedRestDay(null);
    setSpotlightPhotoId(null);
    setHighlightedPhotoMarker(null);
    if (mapRef.current) {
      setSelectedRoute(mapRef.current, null);
      setElevationLocation(mapRef.current, null);
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('activity');
    url.searchParams.delete('day');
    window.history.replaceState(null, '', url);
  };

  const openActivity = (activity: Activity) => {
    const map = mapRef.current;
    const route = routeRef.current;
    if (!map || !route) return;
    selectedActivityRef.current = activity;
    selectedDayDateRef.current = activity.date;
    setSelectedActivity(activity);
    setSelectedRestDay(null);
    setHighlightedPhotoMarker(null);
    setSelectedRoute(map, activity.activity_id, activity.date);
    setElevationLocation(map, null);
    focusActivity(map, route, activity.activity_id, window.matchMedia('(max-width: 700px)').matches);
    const url = new URL(window.location.href);
    url.searchParams.set('activity', activity.activity_id);
    url.searchParams.delete('day');
    window.history.replaceState(null, '', url);
  };

  const openRestDay = (restDay: RestDay) => {
    const map = mapRef.current;
    if (!map) return;
    selectedActivityRef.current = null;
    selectedDayDateRef.current = restDay.date;
    setSelectedActivity(null);
    setSelectedRestDay(restDay);
    setHighlightedPhotoMarker(null);
    setSelectedRoute(map, null, restDay.date);
    setElevationLocation(map, null);
    focusPhotos(
      map,
      photos.filter((photo) => photo.capture_date === restDay.date),
      window.matchMedia('(max-width: 700px)').matches,
    );
    const url = new URL(window.location.href);
    url.searchParams.delete('activity');
    url.searchParams.set('day', restDay.date);
    window.history.replaceState(null, '', url);
  };
  useEffect(() => {
    closeActivityRef.current = closeActivity;
    openActivityRef.current = openActivity;
    openRestDayRef.current = openRestDay;
  });

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: terrainStyle,
      ...japanView,
      attributionControl: false,
      maxPitch: 76,
      renderWorldCopies: window.matchMedia('(max-width: 700px)').matches,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');

    map.once('style.load', async () => {
      map.setTerrain({ source: 'dem', exaggeration: 1.55 });
      const [recordedResult, photoResult] = await Promise.allSettled([
        fetchAndParse('/data/recorded-route.geojson', recordedRouteSchema),
        fetchAndParse('/data/photos.json', photoManifestSchema),
      ]);

      if (photoResult.status === 'fulfilled') setPhotos(photoResult.value.media);

      if (recordedResult.status === 'fulfilled') {
        const route = recordedResult.value;
        routeRef.current = route;
        addRecordedRoute(map, route);
        setSummary(route.metadata);
        setMapReady(true);

        const activitiesById = new Map(
          route.features.map((feature) => [feature.properties.activity_id, feature.properties]),
        );
        setActivities(route.features.map((feature) => feature.properties));
        activitiesByDate.clear();
        for (const feature of route.features) {
          activitiesByDate.set(feature.properties.date, feature.properties);
        }
        map.on('mouseenter', 'recorded-route-hit', () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', 'recorded-route-hit', () => {
          map.getCanvas().style.cursor = '';
        });
        map.on('click', (event) => {
          const feature = map.queryRenderedFeatures(event.point, { layers: ['recorded-route-hit'] })[0];
          if (!feature) {
            if (selectedDayDateRef.current) closeActivityRef.current();
            return;
          }
          const rawId = feature.properties?.activity_id;
          const parsedId = activityIdSchema.safeParse(
            typeof rawId === 'string' || typeof rawId === 'number' ? String(rawId) : rawId,
          );
          const activity = parsedId.success ? activitiesById.get(parsedId.data) : undefined;
          if (!activity) return;
          if (selectedActivityRef.current?.activity_id === activity.activity_id) closeActivityRef.current();
          else openActivityRef.current(activity);
        });

        const requestedActivityId = activityIdSchema.safeParse(
          new URL(window.location.href).searchParams.get('activity'),
        );
        if (requestedActivityId.success) {
          const requestedActivity = activitiesById.get(requestedActivityId.data);
          if (requestedActivity) {
            openActivityRef.current(requestedActivity);
            return;
          }
        }

        const requestedDay = walkDateSchema.safeParse(new URL(window.location.href).searchParams.get('day'));
        const location = requestedDay.success ? restDayLocations[requestedDay.data] : undefined;
        if (requestedDay.success && location) {
          openRestDayRef.current({ date: requestedDay.data, location });
          return;
        }

        focusWholeRoute(map, route, window.matchMedia('(max-width: 700px)').matches, false);
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
      routeRef.current = null;
    };
  }, [activitiesByDate]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !photos.length) return;
    const located = photos.filter(hasPhotoLocation);
    const photoClusters = clusterPhotosByLocation(located);
    const markerElements = photoMarkerElements;
    const clustersByDate = new Map<string, PhotoMarkerCluster[]>();
    for (const cluster of photoClusters) {
      const dayClusters = clustersByDate.get(cluster.anchor.capture_date) ?? [];
      dayClusters.push(cluster);
      clustersByDate.set(cluster.anchor.capture_date, dayClusters);
    }
    const densityTiers = new Map<string, 0 | 1 | 2>();
    for (const dayClusters of clustersByDate.values()) {
      for (const [clusterId, tier] of markerDensityTiers(dayClusters, 1, 3)) {
        densityTiers.set(clusterId, tier);
      }
    }
    const markerClusters = photoClusters;

    let renderedMarkers: Array<{ marker: MapLibreMarker; photoIds: string[] }> = [];

    const clearRenderedMarkers = () => {
      for (const { marker, photoIds } of renderedMarkers) {
        marker.remove();
        for (const photoId of photoIds) markerElements.delete(photoId);
      }
      renderedMarkers = [];
    };

    const openPhoto = (photo: LocatedPhoto) => {
      if (selectedDayDateRef.current !== photo.capture_date) {
        const activity = activitiesByDate.get(photo.capture_date);
        const restLocation = restDayLocations[photo.capture_date];
        if (activity) openActivityRef.current(activity);
        else if (restLocation) openRestDayRef.current({ date: photo.capture_date, location: restLocation });
      }
      setSpotlightPhotoId(photo.id);
    };

    const renderMarkers = () => {
      clearRenderedMarkers();
      const zoom = map.getZoom();
      const maximumTier = zoom >= 7.4 ? 2 : zoom >= 6.1 ? 1 : 0;

      const visiblePhotoMarkers = markerClusters.filter(
        (cluster) => (densityTiers.get(cluster.id) ?? 2) <= maximumTier,
      );
      const clusterRadiusPx = window.matchMedia('(max-width: 700px)').matches ? 40 : 46;
      const visualClusters = clusterPhotoMarkersOnScreen(map, visiblePhotoMarkers, clusterRadiusPx);

      renderedMarkers = visualClusters.flatMap((visualCluster) => {
        const orderedVisualCluster = selectedDayDate
          ? [
              ...visualCluster.filter((cluster) => cluster.anchor.capture_date === selectedDayDate),
              ...visualCluster.filter((cluster) => cluster.anchor.capture_date !== selectedDayDate),
            ]
          : visualCluster;
        const frontLocation = orderedVisualCluster[0];
        if (!frontLocation) return [];
        const photo = frontLocation.anchor;
        const photoIds = orderedVisualCluster.flatMap((cluster) =>
          cluster.photos.map((clusterPhoto) => clusterPhoto.id),
        );
        const expansionCamera =
          orderedVisualCluster.length > 1
            ? photoClusterExpansionCamera(map, orderedVisualCluster, clusterRadiusPx)
            : null;
        const markerKind = expansionCamera ? 'cluster' : photoIds.length > 1 ? 'set' : 'photo';
        const isCurrentDay = orderedVisualCluster.some(
          (cluster) => cluster.anchor.capture_date === selectedDayDate,
        );
        const element = document.createElement('button');
        element.type = 'button';
        element.className = 'trail-photo-marker';
        element.dataset.selected = String(isCurrentDay);
        element.dataset.selectionActive = String(selectedDayDate !== null);
        element.dataset.markerKind = markerKind;
        element.dataset.highlighted = String(photoIds.includes(highlightedPhotoIdRef.current ?? ''));
        const displayedLocations =
          markerKind === 'cluster' ? orderedVisualCluster.slice(0, 3) : [frontLocation];
        element.dataset.stackSize = String(displayedLocations.length);
        element.setAttribute(
          'aria-label',
          markerKind === 'cluster'
            ? `Zoom in to reveal ${visualCluster.length} photo locations`
            : markerKind === 'set'
              ? `Open the first of ${photoIds.length} photos from this location`
              : `Open photo from this location on ${photo.capture_date}`,
        );

        for (const [stackIndex, location] of displayedLocations.entries()) {
          const frame = document.createElement('span');
          frame.className = 'trail-photo-marker-frame';
          frame.dataset.stackIndex = String(stackIndex);
          const image = document.createElement('img');
          image.src = location.anchor.thumbnail_path;
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          frame.append(image);
          element.append(frame);
        }
        if (markerKind === 'set') {
          const signifier = document.createElement('span');
          signifier.className = 'trail-photo-marker-photo-count';
          signifier.setAttribute('aria-hidden', 'true');
          signifier.textContent = String(photoIds.length);
          element.append(signifier);
        }

        // oxlint-disable-next-line unicorn/prefer-add-event-listener -- each disposable marker owns one handler
        element.onclick = (event) => {
          event.stopPropagation();
          if (expansionCamera !== null) {
            map.easeTo({
              center: expansionCamera.center,
              zoom: expansionCamera.zoom,
              duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 700,
            });
            return;
          }
          openPhoto(photo);
        };

        const marker = new maplibregl.Marker({
          element,
          anchor: 'bottom',
          pitchAlignment: 'viewport',
          rotationAlignment: 'viewport',
          subpixelPositioning: true,
        })
          .setLngLat([photo.lon, photo.lat])
          .addTo(map);
        for (const photoId of photoIds) markerElements.set(photoId, element);
        return [{ marker, photoIds }];
      });
    };

    renderMarkers();
    map.on('zoomend', renderMarkers);
    map.on('resize', renderMarkers);

    return () => {
      map.off('zoomend', renderMarkers);
      map.off('resize', renderMarkers);
      clearRenderedMarkers();
    };
  }, [activitiesByDate, mapReady, photoMarkerElements, photos, selectedDayDate]);

  useEffect(() => {
    const map = mapRef.current;
    if (!selectedRestDay || !mapReady || !photos.length || !map) return;
    focusPhotos(
      map,
      photos.filter((photo) => photo.capture_date === selectedRestDay.date),
      window.matchMedia('(max-width: 700px)').matches,
    );
  }, [mapReady, photos, selectedRestDay]);

  const dayPhotos = useMemo(
    () => (selectedDayDate ? photos.filter((photo) => photo.capture_date === selectedDayDate) : []),
    [photos, selectedDayDate],
  );
  const calendarDaySequence: CalendarDay[] = [
    ...activities.map((activity) => ({
      date: activity.date,
      activity,
      restDay: null,
      title: activity.title,
    })),
    ...Object.entries(restDayLocations).map(([date, location]) => ({
      date,
      activity: null,
      restDay: { date, location },
      title: location,
    })),
    // oxlint-disable-next-line unicorn/no-array-sort -- this is a fresh local sequence.
  ].sort((first, second) => first.date.localeCompare(second.date));
  const selectedDayIndex = selectedActivity
    ? calendarDaySequence.findIndex((day) => day.activity?.activity_id === selectedActivity.activity_id)
    : selectedRestDay
      ? calendarDaySequence.findIndex((day) => day.restDay?.date === selectedRestDay.date)
      : -1;
  const previousDay = selectedDayIndex > 0 ? (calendarDaySequence[selectedDayIndex - 1] ?? null) : null;
  const nextDay = selectedDayIndex >= 0 ? (calendarDaySequence[selectedDayIndex + 1] ?? null) : null;

  const openCalendarDay = (day: CalendarDay) => {
    if (day.activity) openActivity(day.activity);
    else if (day.restDay) openRestDay(day.restDay);
  };

  const toggleView = () => {
    const map = mapRef.current;
    if (!map) return;
    const next = !isThreeDimensional;
    setIsThreeDimensional(next);
    map.setTerrain(next ? { source: 'dem', exaggeration: 1.55 } : null);
    map.easeTo({
      pitch: next ? japanView.pitch : 0,
      bearing: next ? japanView.bearing : 0,
      duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 900,
    });
  };

  const showWholeRoute = () => {
    const map = mapRef.current;
    const route = routeRef.current;
    closeActivity();
    if (!map || !route) return;
    focusWholeRoute(map, route, window.matchMedia('(max-width: 700px)').matches);
  };

  return (
    <>
      <Experience $drawerOpen={selectedDayDate !== null}>
        <MapCanvas
          ref={mapContainer}
          aria-label="Interactive 3D map of the recorded bicycle journey from Cape Sata to Cape Soya in Japan"
        />
        <PaperGrain aria-hidden="true" />

        {!selectedDayDate && (
          <StoryPanel aria-live="polite">
            <OverviewTitle>2026 Japan</OverviewTitle>
            <Stats>
              <div>
                <dt>Distance</dt>
                <dd>{summary ? `${wholeNumberFormat.format(summary.distanceKm)} km` : '—'}</dd>
              </div>
              <div>
                <dt>Elevation gain</dt>
                <dd>{summary ? `${numberFormat.format(summary.elevationGainM)} m` : '—'}</dd>
              </div>
              <div>
                <dt>Journey days</dt>
                <dd>
                  {summary ? numberFormat.format(inclusiveDayCount(summary.started, summary.finished)) : '—'}
                </dd>
              </div>
            </Stats>
          </StoryPanel>
        )}

        <MapTools aria-label="Map view controls">
          <button type="button" aria-pressed={isThreeDimensional} onClick={toggleView}>
            {isThreeDimensional ? '3D relief' : '2D atlas'}
          </button>
          <button type="button" onClick={showWholeRoute}>
            Whole route
          </button>
        </MapTools>
      </Experience>

      <ActivityDrawer
        activity={selectedActivity}
        restDay={selectedRestDay}
        photos={dayPhotos}
        journeyPhotos={photos}
        previousDay={previousDay}
        nextDay={nextDay}
        spotlightPhotoId={spotlightPhotoId}
        isMobile={isMobile}
        onClose={closeActivity}
        onNavigate={openCalendarDay}
        onSpotlightClose={() => setSpotlightPhotoId(null)}
        onPhotoMarkerHighlightChange={setHighlightedPhotoMarker}
        onElevationLocationChange={(coordinate) => {
          if (mapRef.current) setElevationLocation(mapRef.current, coordinate);
        }}
      />
    </>
  );
}
